/**
 * CommunityShell — shared warm chrome for the standalone community
 * experience. Every community module renders inside this so it's instantly
 * separate from the student surfaces and NEVER shows the student layout.
 * Provides the sticky top bar (brand + module strip) and the palette.
 */
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";

export const C = {
  INK: "#2B2620", SUB: "#5C5344", FAINT: "#948B7A",
  SKY: "#B06A3C", ACCENT: "#9C5D34", DEEP: "#6E4023", SAGE: "#5F7D61",
  CARD: "#FFFDF8", PANEL: "#F3E9D6", BG: "#FAF5EC", BORDER: "#E3D8C2",
  DISPLAY: "'Fraunces', Georgia, 'Times New Roman', serif",
  SANS: "'Plus Jakarta Sans', system-ui, sans-serif",
};

// The community module strip — the founder's order (Jun 2026):
// all modules visible on one row, no "More" menu.
export const COMMUNITY_MODULES = [
  { to: "/community/symptoms",      label: "Symptoms" },
  { to: "/explain-short",           label: "Explain Short" },
  { to: "/community/results",       label: "Results" },
  { to: "/community/medications",   label: "Medications" },
  { to: "/community/procedures",    label: "Procedures" },
  { to: "/community/visit-prep",    label: "Visit Prep" },
  { to: "/community/visit-summary", label: "Visit Summary" },
  { to: "/community/caregiver",     label: "Caregiver" },
  { to: "/community/cooking",       label: "Cooking" },
  { to: "/community/resources",     label: "Resources" },
  { to: "/community/tutor",         label: "Ask AI" },
];

// "More" — pinned to the top-right corner of row 1; holds Student Home.
function MoreCorner() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const openMenu = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ left: Math.max(8, r.right - 176), top: r.bottom + 4 });
    setOpen(true);
  };
  return (
    <div className="relative shrink-0" onMouseEnter={openMenu} onMouseLeave={() => setOpen(false)}>
      <button
        onClick={(e) => (open ? setOpen(false) : openMenu(e))}
        data-testid="community-nav-more"
        aria-haspopup="true" aria-expanded={open}
        className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-bold whitespace-nowrap transition-all hover:-translate-y-0.5"
        style={{ color: C.INK, fontFamily: C.SANS }}
      >
        More <ChevronDown className="h-3 w-3" />
      </button>
      {open && pos && (
        <div className="fixed w-44 z-50" style={{ left: pos.left, top: pos.top }} data-testid="community-more-menu">
          <div className="rounded-2xl p-2" style={{ background: C.CARD, border: `1px solid ${C.BORDER}`, boxShadow: "0 16px 40px rgba(43,38,32,0.16)" }}>
            <Link
              to="/" data-testid="community-more-student-home"
              className="block rounded-xl px-3 py-2 text-[13px] font-semibold transition-colors hover:bg-[#F3E9D6]"
              style={{ color: C.INK, fontFamily: C.SANS }}
              onClick={() => setOpen(false)}
            >
              Student Home
            </Link>
            <Link
              to="/schools" data-testid="community-more-schools"
              className="block rounded-xl px-3 py-2 text-[13px] font-semibold transition-colors hover:bg-[#F3E9D6]"
              style={{ color: C.INK, fontFamily: C.SANS }}
              onClick={() => setOpen(false)}
            >
              Maldek Schools
            </Link>
            <Link
              to="/event" data-testid="community-more-event-mode"
              className="block rounded-xl px-3 py-2 text-[13px] font-semibold transition-colors hover:bg-[#F3E9D6]"
              style={{ color: C.INK, fontFamily: C.SANS }}
              onClick={() => setOpen(false)}
            >
              Maldek — The Honest Guide
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export function CommunityTopBar() {
  const { pathname } = useLocation();
  return (
    <header
      data-testid="community-top-bar"
      className="sticky top-0 z-40 border-b"
      style={{ background: "rgba(250,245,236,0.96)", backdropFilter: "blur(12px)", borderColor: C.BORDER }}
    >
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6">
        <div className="flex items-stretch gap-3">
          {/* Brand — spans both rows */}
          <Link to="/community" className="flex items-center gap-2.5 shrink-0 py-2" data-testid="community-brand">
            <span
              className="w-8 h-8 rounded-lg overflow-hidden shrink-0"
              style={{ background: "#2b261c", boxShadow: "0 4px 12px rgba(19,28,47,0.25)" }}
            >
              <img
                src="/brand/icon.png"
                alt="Maldek — Tree of Knowledge"
                className="w-full h-full object-cover"
                data-testid="community-brand-logo"
              />
            </span>
            <span className="leading-none">
              <span className="block text-[16px] font-bold tracking-tight" style={{ color: C.INK, fontFamily: C.DISPLAY }}>The Community</span>
              <span className="block text-[8px] font-bold uppercase tracking-[0.22em] mt-0.5" style={{ color: C.ACCENT, fontFamily: C.SANS }}>Understanding your own body</span>
            </span>
          </Link>
          {/* Right column — two aligned rows, width tracks the module strip */}
          <div className="min-w-0 flex flex-col sm:pl-4 sm:border-l" style={{ borderColor: C.BORDER }}>
            {/* Row 1 — primary nav + More, evenly spaced */}
            <nav data-testid="community-primary-nav" className="flex items-center h-11 border-b" style={{ borderColor: C.BORDER }}>
              <div className="flex items-center justify-between gap-1 flex-1 min-w-0 overflow-x-auto no-scrollbar">
                {[
                  { to: "/community", label: "Home" },
                  { to: "/community/how-it-works", label: "How it works" },
                  { to: "/community/facilities", label: "Facilities & Partners" },
                  { to: "/community/analytics", label: "Analytics" },
                ].map((l) => {
                  const active = pathname === l.to;
                  return (
                    <Link
                      key={l.to} to={l.to}
                      data-testid={`community-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="shrink-0 px-3 py-1.5 rounded-full text-[12px] font-bold whitespace-nowrap transition-all hover:-translate-y-0.5"
                      style={active
                        ? { background: C.INK, color: "#FFFDF8", fontFamily: C.SANS }
                        : { color: C.INK, fontFamily: C.SANS }}
                    >
                      {l.label}
                    </Link>
                  );
                })}
                <MoreCorner />
              </div>
            </nav>
            {/* Row 2 — module strip, left-aligned with row 1 */}
            <nav data-testid="community-module-row" className="h-11 flex items-center">
              <div className="flex items-center flex-nowrap gap-1.5 overflow-x-auto no-scrollbar w-full">
                {COMMUNITY_MODULES.map((t) => {
                  const active = pathname === t.to;
                  return (
                    <Link
                      key={t.to} to={t.to}
                      data-testid={`community-top-${t.to.replace(/[/]/g, "-")}`}
                      className="shrink-0 px-2.5 py-1 rounded-full text-[11.5px] font-semibold whitespace-nowrap transition-all hover:-translate-y-0.5"
                      style={active
                        ? { background: C.ACCENT, color: "#FFFDF8", fontFamily: C.SANS }
                        : { background: C.PANEL, color: C.DEEP, border: `1px solid ${C.BORDER}`, fontFamily: C.SANS }}
                    >
                      {t.label}
                    </Link>
                  );
                })}
              </div>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
}

/** Wraps an ORIGINAL module page in the community chrome (top bar, no student header). */
const WRAP_HEROES = {
  "/community/tutor":       { title: "Ask anything, anytime", line: "A friendly AI guide for patients, clinic staff, and students — pick who's asking and get an answer that fits." },
  "/explain-short":          { title: "Explained, simply", line: "Every condition in plain language — what it is, why it happens, and what to do next." },
  "/community/symptoms":    { title: "What could my symptoms mean?", line: "Tell us what you're feeling and see what your body might be saying — and what to ask next." },
  "/community/labs":        { title: "What your numbers mean", line: "Every value on your lab printout, translated into plain English." },
  "/community/cooking":     { title: "Cook for your condition", line: "Dishes you love, gently adapted for your health — flavor intact." },
  "/community/resources":   { title: "Real help near you", line: "Programs and phone numbers for food, housing, coverage, and more." },
  "/community/tutor":       { title: "Ask anything", line: "A friendly AI that explains your health questions in plain language." },
  "/community/household":   { title: "Home life & your health", line: "How money, food, housing, and the people around you shape your body." },
};

export function CommunityWrap({ children }) {
  const { pathname } = useLocation();
  const hero = Object.entries(WRAP_HEROES).find(([p]) => pathname === p || pathname.startsWith(p + "/"))?.[1];
  return (
    <div style={{ background: C.BG, minHeight: "100vh" }}>
      <CommunityTopBar />
      {hero && (
        <section
          className="border-b"
          data-testid="community-module-hero"
          style={{ background: "linear-gradient(165deg, #F6EBD9 0%, #F8F0E2 70%, #FAF5EC 100%)", borderColor: C.BORDER }}
        >
          <div className="max-w-6xl mx-auto px-6 py-6 sm:py-7 flex flex-col sm:flex-row sm:items-baseline gap-1.5 sm:gap-5">
            <h1 className="text-[24px] sm:text-[28px] font-bold tracking-tight leading-tight shrink-0" style={{ color: C.INK, fontFamily: C.DISPLAY }}>
              {hero.title}
            </h1>
            <p className="text-[14px] leading-snug" style={{ color: C.SUB }}>{hero.line}</p>
          </div>
        </section>
      )}
      {children}
    </div>
  );
}

/** Standard community page wrapper: top bar + warm hero header + body. */
export function CommunityShell({ eyebrow, title, intro, children, testid }) {
  return (
    <div data-testid={testid} style={{ background: C.BG, color: C.INK, minHeight: "100vh" }}>
      <CommunityTopBar />
      <section className="border-b" style={{ background: "linear-gradient(165deg, #F6EBD9 0%, #F8F0E2 60%, #FAF5EC 100%)", borderColor: C.BORDER }}>
        <div className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
          {eyebrow && <p className="text-[12px] font-bold uppercase tracking-[0.26em] mb-4" style={{ color: C.ACCENT }}>{eyebrow}</p>}
          <h1 className="font-extrabold tracking-tight leading-[1.06] text-[32px] sm:text-[46px]" style={{ color: C.INK, fontFamily: C.DISPLAY }}>{title}</h1>
          {intro && <p className="mt-5 text-[17px] leading-relaxed max-w-2xl" style={{ color: C.SUB }}>{intro}</p>}
        </div>
      </section>
      <main className="max-w-5xl mx-auto px-6 py-12">{children}</main>
    </div>
  );
}
